export const getMcqChoices = (question) => {
  const raw = question?.choices;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((choice) => {
      if (typeof choice === 'string') {
        return { text: choice.trim(), is_correct: false };
      }
      return {
        text: String(choice?.text || choice?.option || choice?.label || '').trim(),
        is_correct: Boolean(choice?.is_correct || choice?.correct || choice?.isCorrect),
      };
    })
    .filter((choice) => choice.text);
};

export const getQuestionTypeOptions = (t) => [
  { value: 'concept', label: t('jobDescription.conceptual', 'Conceptual') },
  { value: 'coding', label: t('jobDescription.coding', 'Coding') },
  { value: 'mcq', label: t('jobDescription.multipleChoice', 'Multiple Choice') },
  { value: 'behavioral', label: t('jobDescription.behavioral', 'Behavioral') },
  { value: 'general', label: t('jobDescription.general', 'General') },
];

export const getQuestionTypeColor = (type) => {
  const colors = {
    concept: 'bg-purple-100 text-purple-800',
    coding: 'bg-orange-100 text-orange-800',
    mcq: 'bg-green-100 text-green-800',
    behavioral: 'bg-pink-100 text-pink-800',
    general: 'bg-gray-100 text-gray-800',
  };
  return colors[type] || colors.general;
};

export const getQuestionTypeChipStyles = (type, selected) => {
  const styles = {
    concept: selected
      ? 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-200'
      : 'bg-purple-50 text-purple-700 border-purple-100 hover:border-purple-300 hover:bg-purple-100',
    coding: selected
      ? 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-200'
      : 'bg-orange-50 text-orange-700 border-orange-100 hover:border-orange-300 hover:bg-orange-100',
    mcq: selected
      ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:border-emerald-300 hover:bg-emerald-100',
    behavioral: selected
      ? 'bg-pink-600 text-white border-pink-600 shadow-md shadow-pink-200'
      : 'bg-pink-50 text-pink-700 border-pink-100 hover:border-pink-300 hover:bg-pink-100',
    general: selected
      ? 'bg-slate-700 text-white border-slate-700 shadow-md shadow-slate-200'
      : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-100',
  };
  return styles[type] || styles.general;
};

export const bankItemToJdQuestion = (item) => {
  const question = {
    nameOfQuestion: item.text,
    type: item.question_type,
    questionBankId: item.id,
  };
  if (item.question_type === 'coding') {
    question.time_limit = item.time_limit > 0 ? item.time_limit : 5;
  }
  if (item.question_type === 'mcq') {
    question.choices = Array.isArray(item.choices) ? item.choices : [];
  }
  return question;
};

export const emptyQuestionForm = () => ({
  text: '',
  question_type: 'concept',
  time_limit: 5,
  mcqOptions: ['', '', '', ''],
  mcqCorrectIndex: 0,
});

export const formFromBankItem = (item) => {
  const choices = Array.isArray(item.choices) ? item.choices : [];
  const correctIdx = choices.findIndex((choice) => choice.is_correct);
  return {
    text: item.text || '',
    question_type: item.question_type || 'concept',
    time_limit: item.time_limit || 5,
    mcqOptions: [0, 1, 2, 3].map((i) => choices[i]?.text || ''),
    mcqCorrectIndex: correctIdx >= 0 ? correctIdx : 0,
  };
};

export const normalizeBankQuestionType = (type) => {
  const key = String(type || '').trim().toLowerCase();
  const map = {
    conceptual: 'concept',
    concept: 'concept',
    coding: 'coding',
    mcq: 'mcq',
    'multiple choice': 'mcq',
    multiple_choice: 'mcq',
    behavioral: 'behavioral',
    general: 'general',
  };
  return map[key] || 'general';
};

export const payloadFromAiQuestion = (question, timeLimit = 5) => {
  const question_type = normalizeBankQuestionType(question.type || question.question_type);
  const payload = {
    text: String(question.nameOfQuestion || question.question || question.text || '').trim(),
    question_type,
  };

  if (question_type === 'coding') {
    const parsed = Number(timeLimit || question.time_limit || 5);
    payload.time_limit = Number.isFinite(parsed) ? Math.min(60, Math.max(1, parsed)) : 5;
  }

  if (question_type === 'mcq' && Array.isArray(question.choices)) {
    const choices = question.choices.map((choice) => (
      typeof choice === 'string'
        ? { text: choice.trim(), is_correct: false }
        : { text: String(choice?.text || '').trim(), is_correct: Boolean(choice?.is_correct) }
    )).filter((choice) => choice.text);
    if (choices.length && !choices.some((choice) => choice.is_correct)) {
      choices[0].is_correct = true;
    }
    payload.choices = choices;
  }

  if (question.skill_tag) {
    payload.skill_tag = String(question.skill_tag).slice(0, 100);
  }

  return payload;
};

export const findMatchingBankQuestion = (items, text) => {
  const normalized = String(text || '').trim().toLowerCase();
  if (!normalized) return null;
  return (items || []).find((item) => String(item.text || '').trim().toLowerCase() === normalized) || null;
};

export const saveAiQuestionToBank = async (apiClient, question, { companyId, timeLimit } = {}) => {
  const payload = payloadFromAiQuestion(question, timeLimit);
  if (!payload.text) {
    throw new Error('Question text is required');
  }
  if (companyId) payload.company = Number(companyId);

  const params = { search: payload.text.slice(0, 80), page_size: 50 };
  if (companyId) params.company = companyId;

  try {
    const existingResponse = await apiClient.get('question-bank/', { params });
    const results = Array.isArray(existingResponse.data)
      ? existingResponse.data
      : (existingResponse.data?.results || []);
    const match = findMatchingBankQuestion(results, payload.text);
    if (match) return match;
  } catch (error) {
    // Search is best-effort; still attempt to create the bank question.
  }

  const response = await apiClient.post('question-bank/', payload);
  return response.data;
};

export const payloadFromForm = (form) => {
  const payload = {
    text: form.text.trim(),
    question_type: form.question_type,
  };
  if (form.question_type === 'coding') {
    payload.time_limit = form.time_limit > 0 ? form.time_limit : 5;
  }
  if (form.question_type === 'mcq') {
    payload.choices = form.mcqOptions.map((text, i) => ({
      text: text.trim(),
      is_correct: i === form.mcqCorrectIndex,
    }));
  }
  return payload;
};

export const isQuestionFormValid = (form) => {
  if (!form.text.trim()) return false;
  if (form.question_type === 'mcq') {
    return (
      form.mcqOptions.every((option) => option.trim()) &&
      form.mcqCorrectIndex >= 0 &&
      form.mcqCorrectIndex <= 3
    );
  }
  return true;
};
